import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart, Sparkles as SparklesIcon, ShieldCheck, Palette, Camera,
  Tent, Bed, Layers, BedDouble, Coffee, Lightbulb, Gift, Flag, Sprout, Truck, Package,
  Star, ChevronLeft, ChevronRight,
} from "lucide-react";
import Sparkles from "@/components/site/Sparkles";
import Reveal from "@/components/site/Reveal";
import Badge from "@/components/site/Badge";
import ImageCarousel, { pickImages } from "@/components/site/ImageCarousel";
import LoadingSkeleton from "@/components/site/LoadingSkeleton";
import { INCLUDED } from "@/lib/data";
import { useSiteContent, resolveImageUrl } from "@/lib/siteContent";
import { useSeo } from "@/lib/useSeo";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

const TRUST_ICONS = [
  { icon: Heart, label: "Family-Owned" },
  { icon: SparklesIcon, label: "Stress-Free Setup" },
  { icon: ShieldCheck, label: "Clean & Sanitized" },
  { icon: Palette, label: "Custom Themes" },
  { icon: Camera, label: "Picture-Perfect Memories" },
];

const INCLUDED_ICONS = [Tent, Bed, Layers, BedDouble, Coffee, Lightbulb, Gift, Palette, Flag, Sprout, Truck];

export default function HomePage() {
  const { content, loading } = useSiteContent();
  useSeo("Home", "Magical Teepee Sleepovers");
  if (loading || !content) return <LoadingSkeleton />;
  return (
    <>
      <Hero site={content.site} />
      <TrustBar />
      <About site={content.site} />
      <WhatsIncluded />
      <Packages packages={(content.packages || []).filter((p) => p.showOnHome !== false)} />
      <Themes themes={(content.themes || []).filter((t) => t.showOnHome !== false)} />
      <Gallery gallery={content.gallery || []} site={content.site || {}} />
      <WhyFamilies />
      <Testimonials testimonials={content.testimonials || []} />
      <FinalCTA site={content.site} />
    </>
  );
}

function Hero({ site }) {
  return (
    <section data-testid="hero-section" className="relative min-h-[88vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={resolveImageUrl(site.heroImageUrl)} alt="Magical teepee sleepover" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-br from-cozy-brown/60 via-cozy-brown/30 to-cozy-pink/20" />
      </div>
      <Sparkles count={18} />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-32 w-full">
        <div className="max-w-2xl">
          <Reveal>
            <span className="inline-block px-4 py-1.5 rounded-full bg-cozy-pink/90 text-cozy-brown text-xs tracking-widest uppercase font-medium backdrop-blur">
              {site.heroBadge}
            </span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05]">
              <span className="font-script text-cozy-pink text-6xl sm:text-7xl lg:text-8xl block mb-2">{site.heroScriptWord}</span>
              {site.heroTitle}
            </h1>
          </Reveal>
          <Reveal delay={220}>
            <p className="text-cozy-bg/95 text-xl sm:text-2xl font-serif italic mt-4">{site.heroSubtitle}</p>
          </Reveal>
          <Reveal delay={320}>
            <p className="text-white/90 text-base sm:text-lg mt-6 leading-relaxed max-w-xl whitespace-pre-line">{site.heroBody}</p>
          </Reveal>
          <Reveal delay={420}>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/booking" data-testid="hero-cta-book" className="px-7 py-3.5 rounded-full bg-cozy-gold hover:bg-cozy-gold-dark text-white font-medium shadow-cozy-lg transition-all hover:-translate-y-0.5">
                Book Your Party
              </Link>
              <Link to="/themes" data-testid="hero-cta-themes" className="px-7 py-3.5 rounded-full bg-white/95 hover:bg-white text-cozy-brown font-medium shadow-cozy transition-all hover:-translate-y-0.5">
                View Themes
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="py-14 bg-cozy-beige/40 border-y border-cozy-gold/10" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-2 md:grid-cols-5 gap-8">
        {TRUST_ICONS.map(({ icon: Icon, label }, i) => (
          <Reveal key={label} delay={i * 80}>
            <div className="flex flex-col items-center text-center gap-3" data-testid={`trust-item-${i}`}>
              <div className="w-14 h-14 rounded-full bg-white shadow-cozy flex items-center justify-center">
                <Icon className="w-6 h-6 text-cozy-gold" />
              </div>
              <span className="text-sm text-cozy-brown font-medium">{label}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function About({ site }) {
  const paragraphs = (site.aboutBody || "").split(/\n\s*\n/).filter(Boolean);
  return (
    <section className="py-24 lg:py-32" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden shadow-cozy-lg">
            <img src={resolveImageUrl(site.aboutImageUrl)} alt="Family setting up teepee" className="w-full h-[500px] object-cover" loading="lazy" />
          </div>
        </Reveal>
        <Reveal delay={150}>
          <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">About The Cozy Cove</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
            Welcome to <span className="font-script text-cozy-gold">{site.aboutScriptWord || "The Cozy Cove"}</span>
          </h2>
          {paragraphs.map((p, i) => (
            <p key={i} className="text-cozy-brown-soft mt-4 leading-relaxed text-base sm:text-lg">{p}</p>
          ))}
          <Link to="/about" data-testid="about-cta-learn-more" className="inline-flex mt-8 px-7 py-3.5 rounded-full bg-cozy-brown text-cozy-bg hover:bg-cozy-brown-soft transition-all shadow-cozy hover:-translate-y-0.5">
            Learn More About Us
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function WhatsIncluded() {
  return (
    <section className="py-24 bg-cozy-beige/50" data-testid="included-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">What's Included</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4">
              Every <span className="font-script text-cozy-gold">cozy</span> detail, handled
            </h2>
            <p className="text-cozy-brown-soft mt-4">From the fairy lights to the breakfast trays — we bring it all.</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 mt-14">
          {INCLUDED.map((item, i) => {
            const Icon = INCLUDED_ICONS[i % INCLUDED_ICONS.length] || Package;
            return (
              <Reveal key={item} delay={i * 50}>
                <div className="bg-white rounded-2xl p-6 text-center shadow-cozy hover:-translate-y-1 hover:shadow-cozy-lg transition-all border border-cozy-gold/10" data-testid={`included-item-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-cozy-pink/60 mx-auto flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-cozy-gold" />
                  </div>
                  <span className="text-sm text-cozy-brown font-medium">{item}</span>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Packages({ packages }) {
  return (
    <section className="py-24 lg:py-32" id="packages" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">Our Packages</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4">Pick your <span className="font-script text-cozy-gold">perfect</span> party</h2>
            <p className="text-cozy-brown-soft mt-4">Three signature bundles — endless cozy memories.</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-7 mt-14">
          {packages.map((p, i) => {
            const badgeText = (p.badge && p.badge.trim()) || (p.featured ? "Most Popular" : "");
            return (
            <Reveal key={p.id || i} delay={i * 120}>
              <div data-testid={`package-card-${p.id}`} className={`relative rounded-3xl overflow-hidden bg-white shadow-cozy hover:shadow-cozy-lg transition-all hover:-translate-y-2 border ${p.featured ? "border-cozy-gold ring-2 ring-cozy-gold/30" : "border-cozy-gold/15"}`}>
                <Badge text={badgeText} style={p.badgeStyle || "pill"} />
                <ImageCarousel images={pickImages(p)} alt={p.name} aspect="aspect-[4/3]" />
                <div className="p-7">
                  <h3 className="font-serif text-2xl">{p.name}</h3>
                  <p className="text-sm text-cozy-brown-soft mt-1">{p.teepees} Teepees</p>
                  <div className="flex items-baseline gap-1 mt-4">
                    <span className="font-serif text-5xl text-cozy-gold">${p.price}</span>
                  </div>
                  <p className="text-sm text-cozy-brown-soft mt-3 leading-relaxed">{p.description}</p>
                  <Link to={`/booking?package=${p.id}`} data-testid={`package-cta-${p.id}`} className={`mt-6 inline-flex w-full justify-center px-6 py-3 rounded-full font-medium transition-all ${p.featured ? "bg-cozy-gold text-white hover:bg-cozy-gold-dark" : "bg-cozy-brown text-cozy-bg hover:bg-cozy-brown-soft"}`}>
                    Book This Package
                  </Link>
                </div>
              </div>
            </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Themes({ themes }) {
  return (
    <section className="py-24 bg-cozy-pink/30" id="themes" data-testid="themes-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">Signature Themes</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4">Dreamed up for <span className="font-script text-cozy-gold">every</span> kid</h2>
            <p className="text-cozy-brown-soft mt-4">From fairy lights to glow sticks — pick the vibe that fits.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {themes.map((t, i) => (
            <Reveal key={t.id || i} delay={(i % 3) * 100}>
              <Link to="/booking" data-testid={`theme-card-${t.id}`} className="group block rounded-3xl overflow-hidden relative shadow-cozy hover:shadow-cozy-lg transition-all hover:-translate-y-1 border border-cozy-gold/10">
                <Badge text={t.badge} style={t.badgeStyle || "pill"} />
                <ImageCarousel images={pickImages(t)} alt={t.name} aspect="aspect-[4/5]" />
                <div className="absolute inset-0 bg-gradient-to-t from-cozy-brown/85 via-cozy-brown/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-cozy-bg">
                  <h3 className="font-serif text-2xl">{t.name}</h3>
                  <p className="text-sm opacity-90 mt-1 line-clamp-2">{t.description}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery({ gallery, site }) {
  const [lightbox, setLightbox] = useState(null);
  const cols = Math.max(1, Math.min(6, Number(site?.galleryColumns) || 4));
  const rows = Number(site?.galleryRows) || 0;
  const limit = rows > 0 ? cols * rows : gallery.length;
  const shown = gallery.slice(0, limit);
  // Tailwind safelist-friendly explicit classes
  const colClass = {
    1: "lg:columns-1", 2: "lg:columns-2", 3: "lg:columns-3",
    4: "lg:columns-4", 5: "lg:columns-5", 6: "lg:columns-6",
  }[cols];
  return (
    <section className="py-24" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">Gallery</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4"><span className="font-script text-cozy-gold">Moments</span> we've made</h2>
            <p className="text-cozy-brown-soft mt-4">Real Cozy Cove nights, captured.</p>
          </div>
        </Reveal>
        <div className={`masonry columns-2 md:columns-3 ${colClass} mt-12`}>
          {shown.map((src, i) => (
            <button key={i} onClick={() => setLightbox(resolveImageUrl(src))} data-testid={`gallery-item-${i}`} className="block w-full rounded-2xl overflow-hidden shadow-cozy hover:shadow-cozy-lg group">
              <img src={resolveImageUrl(src)} alt={`Gallery ${i+1}`} loading="lazy" className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
            </button>
          ))}
        </div>
      </div>
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl bg-cozy-bg border-cozy-gold/20 p-2 rounded-3xl">
          <VisuallyHidden.Root>
            <DialogTitle>Gallery Image</DialogTitle>
            <DialogDescription>Enlarged view of gallery image</DialogDescription>
          </VisuallyHidden.Root>
          {lightbox && <img src={lightbox} alt="Cozy Cove gallery" className="w-full h-auto rounded-2xl" />}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function WhyFamilies() {
  const items = [
    { icon: Heart, t: "Family-Owned", d: "Run by a family who cares about your family." },
    { icon: SparklesIcon, t: "Stress-Free Setup & Cleanup", d: "We handle everything — you just show up." },
    { icon: ShieldCheck, t: "Clean & Sanitized Equipment", d: "Professionally laundered between every event." },
    { icon: Palette, t: "Customizable Themes", d: "From custom colors to one-of-a-kind themes." },
    { icon: Camera, t: "Picture-Perfect Experiences", d: "Magazine-worthy moments your kids will remember." },
  ];
  return (
    <section className="py-24 bg-cozy-beige/50" data-testid="why-families-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">Why Choose Us</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4">Why families <span className="font-script text-cozy-gold">love</span> us</h2>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mt-14">
          {items.map(({ icon: Icon, t, d }, i) => (
            <Reveal key={t} delay={i * 80}>
              <div className="bg-white rounded-2xl p-7 text-center shadow-cozy hover:-translate-y-1 transition-all border border-cozy-gold/10 h-full" data-testid={`why-item-${i}`}>
                <div className="w-14 h-14 rounded-full bg-cozy-pink/70 mx-auto flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-cozy-gold" />
                </div>
                <h3 className="font-serif text-lg">{t}</h3>
                <p className="text-sm text-cozy-brown-soft mt-2">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ testimonials }) {
  const [idx, setIdx] = useState(0);
  if (!testimonials.length) return null;
  const t = testimonials[idx % testimonials.length];
  const next = () => setIdx((i) => (i + 1) % testimonials.length);
  const prev = () => setIdx((i) => (i - 1 + testimonials.length) % testimonials.length);
  return (
    <section className="py-24 lg:py-32" data-testid="testimonials-section">
      <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
        <Reveal>
          <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">Loved By Parents</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-4">What <span className="font-script text-cozy-gold">families</span> say</h2>
        </Reveal>
        <Reveal delay={150}>
          <div className="mt-14 bg-white rounded-3xl p-10 lg:p-14 shadow-cozy-lg border border-cozy-gold/10 relative" data-testid="testimonial-card">
            <div className="flex justify-center gap-1 mb-5">
              {Array.from({ length: t.rating || 5 }).map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-cozy-gold text-cozy-gold" />
              ))}
            </div>
            <p className="font-serif italic text-2xl lg:text-3xl text-cozy-brown leading-relaxed">"{t.text}"</p>
            <div className="flex items-center justify-center gap-4 mt-8">
              {t.photo && <img src={resolveImageUrl(t.photo)} alt={t.name} className="w-14 h-14 rounded-full object-cover border-2 border-cozy-gold" loading="lazy" />}
              <div className="text-left">
                <div className="font-serif text-lg">{t.name}</div>
                <div className="text-xs text-cozy-brown-soft uppercase tracking-wider">{t.role}</div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mt-8">
              <button onClick={prev} data-testid="testimonial-prev" className="w-10 h-10 rounded-full bg-cozy-beige hover:bg-cozy-gold hover:text-white transition flex items-center justify-center">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)} data-testid={`testimonial-dot-${i}`} className={`w-2 h-2 rounded-full transition ${i === idx ? "bg-cozy-gold w-6" : "bg-cozy-beige"}`} />
                ))}
              </div>
              <button onClick={next} data-testid="testimonial-next" className="w-10 h-10 rounded-full bg-cozy-beige hover:bg-cozy-gold hover:text-white transition flex items-center justify-center">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCTA({ site }) {
  return (
    <section className="relative py-32 overflow-hidden" data-testid="final-cta">
      <img src={resolveImageUrl(site.heroImageUrl)} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-cozy-brown/85 to-cozy-brown/60" />
      <Sparkles count={14} />
      <div className="relative max-w-4xl mx-auto px-5 lg:px-10 text-center text-cozy-bg">
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">{site.finalCtaTitle}</h2>
        </Reveal>
        <Reveal delay={120}>
          <p className="font-serif italic text-xl mt-5 opacity-90">{site.finalCtaSubtitle}</p>
        </Reveal>
        <Reveal delay={220}>
          <Link to="/booking" data-testid="final-cta-book" className="inline-flex mt-8 px-9 py-4 rounded-full bg-cozy-gold hover:bg-cozy-gold-dark text-white text-lg font-medium shadow-cozy-lg transition-all hover:-translate-y-0.5">
            Book Your Cozy Cove Party
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
